import type { GoalConfig } from "@heatmap/shared";
import { clearStoredToken, getStoredToken } from "./auth-storage";
import type {
  ApiErrorBody,
  Goal,
  Heatmap,
  HeatmapQuery,
  HeatmapType,
  LoginResponse,
  MatchType,
  Page,
  PageStats,
  Site,
  Snapshot,
  SnapshotDevice,
  SnapshotRects,
  Tenant,
} from "./types";

// Thin fetch wrapper isolating all HTTP concerns (base URL, auth header, error
// shape) from the rest of the dashboard. If the API contract in
// ARCHITECTURE.md §7 changes, only this file should need edits.

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "";

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

// Called when the API rejects the stored token, so the app can force a re-login.
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  auth?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, auth = true } = options;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (auth) {
    const token = getStoredToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (res.status === 401) {
    clearStoredToken();
    onUnauthorized?.();
    throw new ApiError(401, "認証が切れました。再度ログインしてください。");
  }

  if (!res.ok) {
    let message = `リクエストに失敗しました (${res.status})`;
    try {
      const errBody = (await res.json()) as ApiErrorBody;
      message = errBody.message ?? errBody.error ?? message;
    } catch {
      // response body wasn't JSON; keep default message
    }
    throw new ApiError(res.status, message);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return (await res.json()) as T;
}

// Separate from request<T>() because snapshot images are binary (PNG), not
// JSON — <img src> can't carry the Authorization header, so callers fetch
// the blob here and turn it into an object URL themselves.
async function requestBlob(path: string): Promise<Blob> {
  const headers: Record<string, string> = {};
  const token = getStoredToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${BASE_URL}${path}`, { headers });

  if (res.status === 401) {
    clearStoredToken();
    onUnauthorized?.();
    throw new ApiError(401, "認証が切れました。再度ログインしてください。");
  }

  if (!res.ok) {
    throw new ApiError(res.status, `画像の取得に失敗しました (${res.status})`);
  }

  return res.blob();
}

// ---- Auth ----

export function login(email: string, password: string): Promise<LoginResponse> {
  return request<LoginResponse>("/auth/login", {
    method: "POST",
    body: { email, password },
    auth: false,
  });
}

// ---- Tenants / Sites ----

export function fetchTenants(): Promise<Tenant[]> {
  return request<Tenant[]>("/tenants");
}

export function fetchSites(tenantId: string): Promise<Site[]> {
  return request<Site[]>(`/tenants/${tenantId}/sites`);
}

export interface CreateSiteInput {
  name: string;
  domain: string;
}

export function createSite(tenantId: string, input: CreateSiteInput): Promise<Site> {
  return request<Site>(`/tenants/${tenantId}/sites`, {
    method: "POST",
    body: input,
  });
}

export function fetchSite(siteId: string): Promise<Site> {
  return request<Site>(`/sites/${siteId}`);
}

// ---- Pages ----

export function fetchPages(siteId: string): Promise<Page[]> {
  return request<Page[]>(`/sites/${siteId}/pages`);
}

export interface CreatePageInput {
  name: string;
  urlPattern: string;
  matchType: MatchType;
}

export function createPage(siteId: string, input: CreatePageInput): Promise<Page> {
  return request<Page>(`/sites/${siteId}/pages`, {
    method: "POST",
    body: input,
  });
}

export function deletePage(pageId: string): Promise<void> {
  return request<void>(`/pages/${pageId}`, { method: "DELETE" });
}

// ---- Goals (F6) ----

export function fetchGoals(siteId: string): Promise<Goal[]> {
  return request<Goal[]>(`/sites/${siteId}/goals`);
}

export interface CreateGoalInput {
  name: string;
  config: GoalConfig;
  isMicro: boolean;
  dedup: "session" | "always";
}

export function createGoal(siteId: string, input: CreateGoalInput): Promise<Goal> {
  return request<Goal>(`/sites/${siteId}/goals`, {
    method: "POST",
    body: input,
  });
}

export type UpdateGoalInput = Partial<CreateGoalInput>;

export function updateGoal(goalId: string, input: UpdateGoalInput): Promise<Goal> {
  return request<Goal>(`/goals/${goalId}`, {
    method: "PATCH",
    body: input,
  });
}

export function deleteGoal(goalId: string): Promise<void> {
  return request<void>(`/goals/${goalId}`, { method: "DELETE" });
}

// ---- Heatmap (Phase 1) ----

function heatmapQueryString(query: HeatmapQuery): string {
  const params = new URLSearchParams({
    from: query.from,
    to: query.to,
    device: query.device,
    segment: query.segment,
  });
  return params.toString();
}

export function fetchPage(pageId: string): Promise<Page> {
  return request<Page>(`/pages/${pageId}`);
}

export function fetchHeatmap(pageId: string, type: HeatmapType, query: HeatmapQuery): Promise<Heatmap> {
  return request<Heatmap>(`/pages/${pageId}/heatmap?type=${type}&${heatmapQueryString(query)}`);
}

export function fetchPageStats(pageId: string, query: HeatmapQuery): Promise<PageStats> {
  return request<PageStats>(`/pages/${pageId}/stats?${heatmapQueryString(query)}`);
}

export function fetchSnapshots(pageId: string, device?: SnapshotDevice): Promise<Snapshot[]> {
  const suffix = device ? `?device=${device}` : "";
  return request<Snapshot[]>(`/pages/${pageId}/snapshots${suffix}`);
}

export function fetchSnapshotImageUrl(snapshotId: string): Promise<string> {
  return requestBlob(`/snapshots/${snapshotId}/image`).then((blob) => URL.createObjectURL(blob));
}

export function fetchSnapshotRects(snapshotId: string): Promise<SnapshotRects | null> {
  return request<SnapshotRects>(`/snapshots/${snapshotId}/rects`).catch((err) => {
    if (err instanceof ApiError && err.status === 404) {
      return null;
    }
    throw err;
  });
}

export interface CaptureSnapshotsInput {
  devices?: SnapshotDevice[];
}

export function captureSnapshots(pageId: string, input: CaptureSnapshotsInput = {}): Promise<void> {
  return request<void>(`/pages/${pageId}/snapshots`, {
    method: "POST",
    body: input,
  });
}
